<!--
  Plan.vue — 2026 pricing model plan page (replaces Plan__WithAgencySplit.vue).

  Stripe Checkout + Billing Portal flow: no card form here, just redirects.
  - 4 tier cards (Free / Learn / Growth / Engage — plan keys free/solo/auto/pro), monthly/annual toggle
  - CTA routing depends on how the entity currently pays (purchaseMode):
      checkout — free/trial/canceled: CREATE_BILLING_CHECKOUT → redirect
      portal   — live new-model sub: plan switches happen in the Billing Portal
                 (Checkout would mint a SECOND subscription)
      support  — LTD / grandfathered legacy sub: no self-serve change (legacy
                 entities lack the planStatus guard fields, so Checkout's
                 double-subscribe protection can't see their sub)
  - SMS credit packs via CREATE_CREDIT_PACK_SESSION (one-off Checkout)
  - LTD coupon/deal code box kept (processCouponDealCode callable)
  - entities.upgradeCTAVisible === false hides every purchase CTA (agency-managed
    accounts); collaborators never see purchase controls
-->
<template>
  <div id="plan-page">

    <div class="vx-row">
      <div class="vx-col w-full mb-base" v-if="checkoutSuccess">
        <vs-alert active="true" color="success" icon-pack="feather" icon="icon-check-circle">
          Payment received — thank you! Your plan activates within a few moments. Refresh this page if it doesn't show yet.
        </vs-alert>
      </div>
    </div>

    <!-- current plan -->
    <div class="vx-row">
      <div class="vx-col w-full mb-base">
        <vx-card>
          <div class="flex flex-wrap items-center justify-between">
            <div class="my-2">
              <p>YOUR CURRENT PLAN</p>
              <p class="font-medium text-3xl mt-2 text-primary">{{ currentPlanName }}</p>
              <p class="text-dark mt-1" v-if="statusLine">{{ statusLine }}</p>
            </div>
            <div class="my-2" v-if="showPortalButton">
              <vs-button @click="openPortal" type="border">Manage billing</vs-button>
              <p class="text-dark mt-2 text-sm">Invoices, payment method, plan changes & cancellation</p>
            </div>
          </div>
        </vx-card>
      </div>
    </div>

    <!-- monthly / annual toggle -->
    <div class="vx-row" v-if="ctasVisible">
      <div class="vx-col w-full mb-base flex items-center justify-center">
        <span class="mr-3" :class="{ 'font-semibold': !annual }">Monthly</span>
        <vs-switch v-model="annual" />
        <span class="ml-3" :class="{ 'font-semibold': annual }">Annual <span class="text-success font-semibold">(2 months free)</span></span>
      </div>
    </div>

    <!-- tier cards -->
    <div class="vx-row">
      <div class="vx-col md:w-1/4 sm:w-1/2 w-full my-2" v-for="tier in tiers" :key="tier.key">
        <vx-card class="text-center h-full" :class="{ 'current-tier-card': isCurrentTier(tier) }">
          <p class="font-semibold text-lg">{{ tier.name }}</p>
          <p class="font-medium text-4xl my-4 text-primary">
            {{ tier.key === 'free' ? '$0' : (annual ? '$' + tier.priceY : '$' + tier.priceM) }}<span class="text-lg text-dark">/{{ annual && tier.key !== 'free' ? 'yr' : 'mo' }}</span>
          </p>
          <p class="text-dark mb-4 text-sm">{{ tier.tagline }}</p>
          <div class="text-left mb-6">
            <p class="my-2 text-sm" v-for="(f, i) in tier.features" :key="i">✓ {{ f }}</p>
          </div>
          <div v-if="ctasVisible">
            <vs-button v-if="isCurrentTier(tier)" class="w-full" disabled>Current plan</vs-button>
            <vs-button v-else-if="tier.key === 'free' && purchaseMode === 'portal'" class="w-full" type="border" @click="openPortal">Downgrade via billing</vs-button>
            <span v-else-if="tier.key === 'free'"></span>
            <vs-button v-else-if="purchaseMode === 'checkout'" class="w-full" @click="subscribe(tier)">Choose {{ tier.name }}</vs-button>
            <vs-button v-else-if="purchaseMode === 'portal'" class="w-full" type="border" @click="openPortal">Switch via billing</vs-button>
            <a v-else class="block" :href="supportMailto(tier)"><vs-button class="w-full" type="border">Contact us to switch</vs-button></a>
          </div>
        </vx-card>
      </div>
    </div>

    <!-- credit packs + coupon -->
    <div class="vx-row mt-4">

      <div class="vx-col flex md:w-1/2 w-full my-2" v-if="!$store.getters['dash/isCollaborator']">
        <vx-card class="mb-base">
          <p>SMS CREDIT PACKS</p>
          <p class="my-4">Text/SMS invites use add-on credits on every plan. Buy a pack below — credits never expire.</p>
          <vs-table max-items="4" :data="creditPacks">
            <template slot="thead">
              <vs-th>Credits</vs-th>
              <vs-th>Cost</vs-th>
              <vs-th></vs-th>
            </template>
            <template slot-scope="{data}">
              <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                <vs-td :data="data[indextr].credits">{{ data[indextr].credits }}</vs-td>
                <vs-td :data="data[indextr].cost">{{ data[indextr].cost }}</vs-td>
                <vs-td :data="data[indextr].pack">
                  <vs-button class="float-right ml-1" type="border" @click="buyCredits(data[indextr].pack)">Checkout ></vs-button>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </vx-card>
      </div>

      <div class="vx-col flex md:w-1/2 w-full my-2" v-if="showCouponBox">
        <vx-card class="mb-base">
          <p>COUPON/DEAL CODE</p>
          <p class="my-4">Have a deal or coupon code? Enter it below to be processed. (Discount codes for subscriptions can be entered on the payment page.)</p>
          <vs-input v-model="couponCode" class="w-full" placeholder="Enter code..." />
          <vs-button @click="processCouponCode" :disabled="couponCode.trim() === ''" class="w-full" type="line">Process Code</vs-button>
        </vx-card>
      </div>

    </div>

  </div>
</template>

<script>
export default {
  data () {
    return {
      annual: false,
      couponCode: '',
      checkoutSuccess: false,
      tiers: [
        {
          key: 'free',
          name: 'Free',
          priceM: 0,
          priceY: 0,
          tagline: 'Free until it works',
          features: [
            '20 invites/month (email + SMS, one shared pool)',
            '20 outcomes unlocked, then upgrade',
            '10 imported online reviews',
            '5 video testimonials',
            'Unlimited widgets & referral program',
            'Send from your own email address or Twilio',
            '"Powered by Feedback Link" branding always shown'
          ]
        },
        {
          key: 'solo',
          name: 'Learn',
          priceM: 9,
          priceY: 90,
          tagline: 'For owner-operators',
          features: [
            '100 invites/month',
            'Unlimited outcomes — nothing locked',
            'Unlimited imported reviews',
            '50 video testimonials',
            'Turn off Feedback Link branding',
            'Custom domain for your feedback link',
            'Send from your own email address or Twilio'
          ]
        },
        {
          key: 'auto',
          name: 'Growth',
          priceM: 29,
          priceY: 290,
          tagline: 'For growing businesses',
          features: [
            '1,000 invites/month',
            'Unlimited outcomes — nothing locked',
            'Everything in Learn',
            'Up to 5 spaces (locations)',
            'Developer API access',
            '200 video testimonials'
          ]
        },
        {
          key: 'pro',
          name: 'Engage',
          priceM: 79,
          priceY: 790,
          tagline: 'For multi-location',
          features: [
            'Unlimited invites (fair use)',
            'Unlimited outcomes — nothing locked',
            'Everything in Growth',
            'Up to 10 spaces (locations)',
            '1,000 video testimonials'
          ]
        }
      ],
      creditPacks: [
        { credits: '500', cost: '$9', pack: '500c' },
        { credits: '2000', cost: '$29', pack: '2000c' },
        { credits: '5000', cost: '$69', pack: '5000c' },
        { credits: '10000', cost: '$119', pack: '10000c' }
      ]
    }
  },
  computed: {
    planResolved () {
      return this.$store.getters['dash/planResolved']
    },
    business () {
      return this.$store.state.dash.viewData.business
    },
    isNewPaidKey () {
      return ['solo_m', 'solo_y', 'auto_m', 'auto_y', 'pro_m', 'pro_y'].indexOf(this.planResolved.planKey) !== -1
    },
    //how this entity changes plan — see header comment
    purchaseMode () {
      const r = this.planResolved
      if (this.isNewPaidKey && (r.status === 'active' || r.status === 'past_due')) return 'portal'
      if (r.isLTD) return 'support'
      if (r.planKey !== 'free' && !r.isTrial && !this.isNewPaidKey && this.business.subStatusOK === true) return 'support'
      return 'checkout'
    },
    currentPlanName () {
      const r = this.planResolved
      if (r.isTrial) return 'Free Trial'
      if (r.isLTD) return 'Lifetime Deal'
      if (this.isNewPaidKey && (r.status === 'canceled' || r.status === 'unpaid')) return 'Free (subscription ended)'
      const names = { free: 'Free', solo: 'Learn', auto: 'Growth', pro: 'Engage' }
      if (this.isNewPaidKey || r.planKey === 'free') return names[r.tier] || 'Free'
      return r.planString || 'Legacy plan'
    },
    statusLine () {
      const r = this.planResolved
      if (r.isTrial) {
        return r.isTrialEnded ? 'Your trial has ended — choose a plan below to continue.' : (r.trialDaysLeft + ' day' + (r.trialDaysLeft === 1 ? '' : 's') + ' left of trial')
      }
      if (r.status === 'past_due') return 'Payment past due — please update your payment method via Manage billing.'
      if (this.isNewPaidKey && (r.status === 'canceled' || r.status === 'unpaid')) return 'Your subscription has ended; you\'re on the Free plan. Re-subscribe below any time.'
      if (this.isNewPaidKey && this.planResolved.isAnnual) return 'Billed annually'
      if (this.isNewPaidKey) return 'Billed monthly'
      return false
    },
    ctasVisible () {
      if (this.$store.getters['dash/isCollaborator']) return false
      return this.business.upgradeCTAVisible !== false
    },
    showPortalButton () {
      if (!this.ctasVisible) return false
      //anyone with a Stripe customer (new flow or legacy Elements flow) can use the portal
      return !!(this.business.stripeCustomerID || (this.business.stripeCustomerOject && this.business.stripeCustomerOject.id))
    },
    showCouponBox () {
      if (this.$store.getters['dash/isCollaborator']) return false
      return this.purchaseMode === 'checkout' && !this.planResolved.isLTD
    }
  },
  mounted () {
    if (this.$route.query.checkout === 'success') {
      this.checkoutSuccess = true
    }
  },
  methods: {
    isCurrentTier (tier) {
      const r = this.planResolved
      //a canceled new-model sub resolves to free limits — Free is the current tier
      if (this.isNewPaidKey && (r.status === 'canceled' || r.status === 'unpaid')) return tier.key === 'free'
      return r.tier === tier.key && (this.isNewPaidKey || r.planKey === 'free')
    },
    subscribe (tier) {
      const planKey = tier.key + (this.annual ? '_y' : '_m')
      this.$vs.loading()
      this.$store.dispatch('dash/CREATE_BILLING_CHECKOUT', {
        planKey,
        customerEmail: this.$store.getters['auth/userEmail']
      }).then((r) => {
        window.location.href = r.url
      }).catch((err) => {
        this.$vs.loading.close()
        if (err === 'alreadysubscribed') {
          this.$vs.notify({
            title: 'You already have a subscription',
            text: 'Use "Manage billing" to switch plans — it changes your existing subscription instead of starting a second one.',
            color: 'warning', time: 20000, iconPack: 'feather', icon: 'icon-alert-circle'
          })
        } else {
          this.$vs.notify({
            title: 'Checkout',
            text: 'Something went wrong starting checkout. Please try again or reach out to support.',
            color: 'danger', time: 20000, iconPack: 'feather', icon: 'icon-alert-circle'
          })
        }
      })
    },
    openPortal () {
      this.$vs.loading()
      this.$store.dispatch('dash/OPEN_BILLING_PORTAL').then((r) => {
        window.location.href = r.url
      }).catch((err) => {
        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Billing',
          text: err === 'nocustomer'
            ? 'No billing profile yet — subscribe to a plan first.'
            : 'Couldn\'t open the billing portal. Please try again or reach out to support.',
          color: 'warning', time: 20000, iconPack: 'feather', icon: 'icon-alert-circle'
        })
      })
    },
    buyCredits (pack) {
      this.$vs.loading()
      this.$store.dispatch('dash/CREATE_CREDIT_PACK_SESSION', {
        pack,
        customerEmail: this.$store.getters['auth/userEmail']
      }).then((r) => {
        window.location.href = r.url
      }).catch(() => {
        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Purchase credits',
          text: 'Something went wrong, and we couldn\'t initiate the purchase process. Please try again or reach out to support.',
          color: 'danger', time: 20000, iconPack: 'feather', icon: 'icon-alert-circle'
        })
      })
    },
    supportMailto (tier) {
      const subject = encodeURIComponent('Plan change request: ' + tier.name)
      const body = encodeURIComponent('Hi! I\'d like to change my plan to ' + tier.name + '. My business ID is ' + this.business.businessID + '.')
      return 'mailto:support@feedback.link?subject=' + subject + '&body=' + body
    },
    processCouponCode () {
      this.$vs.loading()
      this.$store.dispatch('dash/PROCESS_DEAL_ADDON_CODE', {
        code: this.couponCode,
        type: 'plan'
      }).then((res) => {
        if (res.data.valid) {
          if (res.data.type === 'ltd') {
            this.$router.push(`/dash/home?${res.data.partner}=true`)
          } else if (res.data.type === 'partnersubdiscount') {
            this.$vs.notify({
              title: 'Discount code',
              text: 'This discount applies at checkout — choose a plan above and enter the code in the promo-code field on the payment page.',
              color: 'success', time: 20000, iconPack: 'feather', icon: 'icon-check'
            })
            this.couponCode = ''
          }
        } else {
          this.$vs.notify({
            title: 'Deal Code Invalid',
            text: 'Please double check your code text. Reach out to support if this issue continues.',
            color: 'warning', time: 20000, iconPack: 'feather', icon: 'icon-alert-circle'
          })
        }
        this.$vs.loading.close()
      }).catch(() => {
        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Deal Code',
          text: 'Please double check your code text. Reach out to support if this issue continues.',
          color: 'warning', time: 20000, iconPack: 'feather', icon: 'icon-alert-circle'
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.current-tier-card {
  border: 2px solid rgba(var(--vs-primary), 1);
}
</style>
